import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Plus, Search, MapPin, Calendar, ChevronRight } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import api from '../utils/api';
import dayjs from 'dayjs';

export default function TripReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = () => {
    api.get('/trips', { params: { page, limit: 20, search } }).then(r => {
      setReports(r.data.reports || []);
      setTotal(r.data.total || 0);
    });
  };

  useEffect(() => { load(); }, [page, search]);

  const doSearch = () => { setPage(1); setSearch(searchInput); };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <PageHeader icon={Plane} title="출장보고" count={total || undefined}>
        <button
          onClick={() => navigate('/trips/new')}
          className="flex items-center gap-1.5 bg-brand text-white text-sm px-4 py-2 rounded-lg hover:bg-brand/80"
        >
          <Plus size={15} /> 새 출장보고
        </button>
      </PageHeader>

      {/* 검색 */}
      <div className="flex gap-2 mb-6 flex-shrink-0">
        <div className="flex-1 flex items-center bg-gray-900 border border-gray-800 rounded-lg px-3">
          <Search size={14} className="text-gray-500 flex-shrink-0" />
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="제목 또는 목적지 검색..."
            className="flex-1 bg-transparent text-white text-sm py-2 px-2 focus:outline-none"
          />
        </div>
        <button onClick={doSearch} className="px-4 py-2 bg-gray-700 text-gray-300 text-sm rounded-lg hover:bg-gray-600">
          검색
        </button>
      </div>

      {/* 목록 */}
      <div className="flex-1 overflow-y-auto">
        {reports.length === 0 ? (
          <div className="text-center py-20 text-gray-600 text-sm">
            {search ? '검색 결과가 없습니다.' : '아직 출장보고가 없습니다.'}
          </div>
        ) : (
          <div className="grid gap-3">
            {reports.map(r => (
              <div
                key={r.id}
                onClick={() => navigate(`/trips/${r.id}`)}
                className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 cursor-pointer transition-all group flex"
              >
                {/* 대표사진 */}
                {r.coverPhoto ? (
                  <div className="w-36 flex-shrink-0">
                    <img
                      src={r.coverPhoto}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-36 flex-shrink-0 bg-gray-800 flex items-center justify-center">
                    <Plane size={24} className="text-gray-700" />
                  </div>
                )}

                {/* 내용 */}
                <div className="flex-1 min-w-0 p-5 flex flex-col justify-between">
                  <div>
                    <h2 className="text-white font-semibold text-base truncate group-hover:text-brand transition-colors mb-2">
                      {r.title}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <span className="text-gray-400 font-medium">{r.author?.name || r.author?.username}</span>
                      {r.destination && (
                        <span className="flex items-center gap-1">
                          <MapPin size={11} />
                          {r.destination}
                        </span>
                      )}
                      {r.startDate && (
                        <span className="flex items-center gap-1">
                          <Calendar size={11} />
                          {dayjs(r.startDate).format('YYYY.MM.DD')}
                          {r.endDate && r.endDate !== r.startDate && ` ~ ${dayjs(r.endDate).format('MM.DD')}`}
                        </span>
                      )}
                    </div>
                    {r.purpose && (
                      <p className="text-gray-500 text-xs mt-2 line-clamp-2">{r.purpose}</p>
                    )}
                  </div>
                  <span className="text-gray-700 text-[11px] mt-3">{dayjs(r.createdAt).format('YYYY.MM.DD')}</span>
                </div>

                <div className="flex items-center pr-4">
                  <ChevronRight size={16} className="text-gray-700 group-hover:text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 pb-4">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
              .map((p, i, arr) => (
                <span key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && <span className="text-gray-600 px-1">…</span>}
                  <button
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-sm ${p === page ? 'bg-brand text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                  >
                    {p}
                  </button>
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
